import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Send } from "lucide-react";
import { MicButton } from "@/components/MicButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StorySubmit() {
  const [form, setForm] = useState({ name: "", role: "", email: "", quote: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please tick the permission box so we can share your story.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/stories/submit`, form);
      setSent(true);
      toast.success("Story received. We'll read every word.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#FFFFFF] border border-[#E7DFD5] px-4 py-3.5 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B] transition-colors duration-300";

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
      className="mt-24 md:mt-32 border border-[#E7DFD5] p-8 md:p-12"
      data-testid="story-submit-section"
    >
      <div className="grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 md:col-span-5">
          <div className="section-tag mb-6">§ Send us your story</div>
          <h3 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-tight mb-4">
            You read the book. It <span className="italic text-[#B45309]">did something</span>.
          </h3>
          <p className="text-[#78716C] text-sm leading-relaxed max-w-md">
            Tell us how, in your words. Approved stories replace the placeholder Field Notes above — attributed the way you choose (full name, first name only, or role).
          </p>
        </div>

        <div className="col-span-12 md:col-span-7">
          {sent ? (
            <div data-testid="story-submit-success" className="p-8 border border-[#F59E0B]/40 bg-[#F59E0B]/5">
              <p className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-3">Received.</p>
              <p className="text-[#78716C] text-sm leading-relaxed">Rich reads every one. If your story is approved you&apos;ll see it appear in the Field Notes above.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="story-submit-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">Your name</span>
                  <input required data-testid="story-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="First and last, or just first" />
                </label>
                <label className="block">
                  <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">What you do</span>
                  <input data-testid="story-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} placeholder="Night nurse, warehouse lead, EMT…" />
                </label>
              </div>
              <label className="block">
                <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">Email (private — never published)</span>
                <input required type="email" data-testid="story-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@work.email" />
              </label>
              <label className="block">
                <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">Your story (2–4 sentences)</span>
                <textarea required minLength={20} maxLength={600} data-testid="story-quote" rows={5} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className={`${inputCls} resize-none`} placeholder="What changed after you read the book or ran the modules. Honest, plain words — that&apos;s the point." />
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <MicButton
                      testId="story-mic"
                      size={40}
                      context="A worker telling their own story about what changed after T.A.C.C."
                      onText={(t) => setForm((f) => ({ ...f, quote: `${f.quote ? f.quote + " " : ""}${t}`.slice(0, 600) }))}
                    />
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#78716C]">Speak your story — we'll type it</span>
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E]">{form.quote.length} / 600</div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="story-consent-label">
                <input required type="checkbox" data-testid="story-consent" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-[#F59E0B]" />
                <span className="text-xs text-[#78716C] leading-relaxed">
                  I give T.A.C.C. permission to publish this story on the site with the name and role I chose above. I can ask you to remove it any time by emailing back.
                </span>
              </label>
              <div className="pt-2">
                <button type="submit" disabled={loading} data-testid="story-submit-btn" className="btn-ember text-sm">
                  {loading ? "Sending…" : "Send my story"}
                  {loading ? <ArrowUpRight size={16} /> : <Send size={15} />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
}
